import { FEATURE_FLAGS, type FeatureFlag, getFeatureFlagDisplayName } from '@core/domain';
import { useFeatureFlags } from '@core/hooks';
import { Switch, Button } from '@soteria/design-system-ve';
import React from 'react';

import { settingsOptionContainer, settingsOptionLabel } from './SettingsDialog.css';

/**
 * Props for FeatureFlagSettings component
 */
export interface FeatureFlagSettingsProps {
  /**
   * Callback when a feature is toggled (for analytics/logging)
   */
  onFeatureToggle?: (flag: FeatureFlag, enabled: boolean) => void;
}

/**
 * Feature flag settings component for the settings dialog
 *
 * This component provides a user interface for managing feature flags,
 * using the same styling as other settings sections.
 */
export function FeatureFlagSettings({ onFeatureToggle }: FeatureFlagSettingsProps): React.ReactElement {
  const { featureFlags, toggleFeatureFlag, resetFeatureFlags } = useFeatureFlags();

  const handleToggle = (featureFlag: FeatureFlag, isEnabled: boolean) => {
    toggleFeatureFlag(featureFlag, isEnabled);

    if (onFeatureToggle) {
      onFeatureToggle(featureFlag, isEnabled);
    }
  };

  const renderFeatureToggle = (featureFlag: FeatureFlag) => {
    const isEnabled = !!featureFlags[featureFlag];
    const displayName = getFeatureFlagDisplayName(featureFlag);

    return (
      <div key={featureFlag} className={settingsOptionContainer}>
        <div className={settingsOptionLabel}>{displayName}</div>
        <Switch checked={isEnabled} label={`Toggle ${displayName}`} onChange={enabled => handleToggle(featureFlag, enabled)} />
      </div>
    );
  };

  return (
    <div>
      {Object.values(FEATURE_FLAGS).map(renderFeatureToggle)}
      <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
        <Button onClick={() => resetFeatureFlags()}>RESET ALL</Button>
      </div>
    </div>
  );
}
